//
// AHT Weight Management — Oral Semaglutide (Wegovy® tablets) shared screening
// downstream. Single source of truth for the post-confirmation steps shared
// by the initial supply pathway: oral suitability screening, PGD fresh
// checks, interaction checks, cautions review, supply attestation, and the
// shared outcomes.
// Source: AHT_Oral_Wegovy_Supply_Pathway_v1_0.docx
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.

import type { OutcomeNode, PathwayNode } from "../../../types/pathway.types";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

export const OSEM_ORAL_SUITABILITY_SWALLOW = "osem-init-oral-suitability-swallow";
export const OSEM_ORAL_SUITABILITY_FAST = "osem-init-oral-suitability-fast";
export const OSEM_PGD_FRESH_CHECKS = "osem-init-pgd-fresh-checks";
export const OSEM_INT_LEVOTHYROXINE = "osem-init-int1-levothyroxine";
export const OSEM_INT_WARFARIN = "osem-init-int2-warfarin";
export const OSEM_INT_NTI_ORAL = "osem-init-int3-nti-oral";
export const OSEM_INT_CONTRACEPTIVES = "osem-init-int4-contraceptives";
export const OSEM_CAUTIONS = "osem-init-cautions";
export const OSEM_ATTEST = "osem-init-dose-confirm";

// ─────────────────────────────────────────────────────────────────────────────
// Outcome IDs
// ─────────────────────────────────────────────────────────────────────────────

export const OSEM_OUT_CANNOT_SUPPLY = "OSEM_OUT_CANNOT_SUPPLY";
export const OSEM_OUT_SUPPLY = "OSEM_OUT_SUPPLY";

// ─────────────────────────────────────────────────────────────────────────────
// Shared downstream nodes — spread into a pathway's nodes array after the
// confirmation / exclusion-question stage. The first node is
// OSEM_ORAL_SUITABILITY_SWALLOW.
// ─────────────────────────────────────────────────────────────────────────────

export const oralSemaglutideScreeningDownstreamNodes: PathwayNode[] = [
  // ───────────────────────────────────────────────────────────────────────────
  // Oral suitability screening — unique to the tablet formulation
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: OSEM_ORAL_SUITABILITY_SWALLOW,
    type: "yes_no",
    text: "Can the patient swallow the tablet whole, without splitting, crushing or chewing?",
    hint: "Splitting, crushing, or chewing the tablet affects absorption and is not permitted. If the patient cannot swallow the tablet whole, oral semaglutide is not suitable.",
    yes: OSEM_ORAL_SUITABILITY_FAST,
    no: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
  },

  {
    id: OSEM_ORAL_SUITABILITY_FAST,
    type: "yes_no",
    text: "Can the patient reliably take the tablet on waking after a fast of at least 8 hours, with up to 120 mL of water, then wait at least 30 minutes before any food, drink, or other oral medicine?",
    hint: "Waiting less than 30 minutes reduces absorption significantly. If the patient cannot reliably follow this regimen, oral semaglutide is not suitable.",
    yes: OSEM_PGD_FRESH_CHECKS,
    no: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 2b — PGD inclusion / exclusion checks
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: OSEM_PGD_FRESH_CHECKS,
    type: "pgd_exclusion_check",
    variant: "sequential",
    pgdIdOverride: "pgd-aht-wt-oral-semaglutide",
    anyTriggered: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
    allCleared: OSEM_INT_LEVOTHYROXINE,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 3 — Interaction checks
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: OSEM_INT_LEVOTHYROXINE,
    type: "yes_no",
    text: "Is the patient taking levothyroxine?",
    hint: "Oral semaglutide raises total thyroxine exposure by approximately one third. Monitor thyroid function parameters. Levothyroxine also requires an empty stomach — arrange a dosing schedule that separates levothyroxine from the semaglutide fasting window.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document the monitoring plan for thyroid function and the dosing schedule agreed to separate levothyroxine from the semaglutide fasting window",
      acknowledgementText: "Patient has been counselled on this interaction and a monitoring and dosing plan is in place",
    },
    yes: OSEM_INT_WARFARIN,
    no: OSEM_INT_WARFARIN,
    autoAnswer: { when: { hasMedication: ["levothyroxine"] }, value: true },
  },

  {
    id: OSEM_INT_WARFARIN,
    type: "yes_no",
    text: "Is the patient taking warfarin or another coumarin anticoagulant (e.g. acenocoumarol)?",
    hint: "Monitor INR frequently on initiation. Decreased INR has been reported with acenocoumarol.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document the INR monitoring plan agreed for this patient",
      acknowledgementText: "Patient has been counselled on this interaction and INR monitoring is planned",
    },
    yes: OSEM_INT_NTI_ORAL,
    no: OSEM_INT_NTI_ORAL,
    autoAnswer: { when: { hasMedication: ["warfarin", "acenocoumarol"] }, value: true },
  },

  {
    id: OSEM_INT_NTI_ORAL,
    type: "yes_no",
    text: "Is the patient taking any oral medicines with a narrow therapeutic index (e.g. ciclosporin, digoxin, lithium, phenytoin, theophylline)?",
    hint: "Tablets taken within the 30-minute window reduce oral semaglutide absorption by approximately one third. Semaglutide also delays gastric emptying, which can affect absorption of other oral medicines. Enforce the 30-minute separation and monitor drug response and levels.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document the monitoring plan and confirm the 30-minute dosing window separation has been arranged",
      acknowledgementText: "Patient has been counselled on the 30-minute separation requirement and a monitoring plan is in place",
    },
    yes: OSEM_INT_CONTRACEPTIVES,
    no: OSEM_INT_CONTRACEPTIVES,
    autoAnswer: { when: { hasMedication: ["ciclosporin", "digoxin", "lithium", "phenytoin", "theophylline"] }, value: true },
  },

  {
    id: OSEM_INT_CONTRACEPTIVES,
    type: "yes_no",
    text: "Is the patient using oral contraceptives?",
    hint: "Oral semaglutide does not reduce exposure of oral contraceptives (no routine barrier method required for the drug interaction itself). However, vomiting within 3 hours of the pill, or severe diarrhoea lasting more than 24 hours, can reduce reliability. Advise missed-pill guidance and barrier cover until resolved.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document that the patient has been counselled on contraceptive reliability during GI side effects",
      acknowledgementText: "Patient has been counselled on oral contraceptive reliability and vomiting/diarrhoea guidance",
    },
    yes: OSEM_CAUTIONS,
    no: OSEM_CAUTIONS,
    autoAnswer: { when: { patientIsFemale: false }, value: false },
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 4 — Cautions review
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: OSEM_CAUTIONS,
    type: "flag",
    variant: "warn",
    message:
      "Review cautions before supply:\n" +
      "• GI symptoms (nausea, vomiting, diarrhoea) — dehydration risk; maintain hydration, especially in the elderly and those with renal impairment\n" +
      "• Gastroparesis symptoms (bloating, abdominal pain) — discontinue if develops\n" +
      "• Pancreatitis suspected — stop immediately; do not restart if confirmed; enzyme elevation alone does not confirm\n" +
      "• NAION (non-arteritic anterior ischaemic optic neuropathy) — advise patient to report any sudden loss of vision including partial; refer urgently and discontinue if confirmed\n" +
      "• Monitor resting heart rate; report mood or behavioural changes\n" +
      "• Not a substitute for insulin; must not be used in type 1 diabetes or diabetic ketoacidosis\n" +
      "• Aspiration risk — advise of delayed gastric emptying before general anaesthesia or deep sedation; patient must inform anaesthetist/surgical team\n" +
      "• Renal function may deteriorate with prolonged GI effects — monitor",
    next: OSEM_ATTEST,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 5 — Supply attestation
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: OSEM_ATTEST,
    type: "attestation",
    text: "Confirm supply of Wegovy® 1.5 mg tablets (initiation dose)",
    attestationStatement:
      "I confirm that I have:\n" +
      "• Supplied 1 × Wegovy® 1.5 mg tablets, 30-tablet pack\n" +
      "• Checked blister integrity and expiry date (no cold chain applies)\n" +
      "• Confirmed storage: original blister, protected from moisture and light; no special temperature conditions, no refrigeration required\n" +
      "• Counselled the patient on the administration regimen: on waking after a fast of at least 8 hours, swallow whole with up to 120 mL water, wait at least 30 minutes before any food, drink or other oral medicine; one tablet daily\n" +
      "• Counselled the patient on missed dose guidance: skip the missed dose and take the next dose the following day; never take two tablets in one day\n" +
      "• Advised the patient to inform their GP of initiation, and notified the GP\n" +
      "• Provided patient information leaflet\n" +
      "• Recorded batch number and expiry date",
    conclusions: {
      optionA: { label: "Confirm and supply", next: { outcomeId: OSEM_OUT_SUPPLY } },
      optionB: { label: "Do not supply", next: { outcomeId: OSEM_OUT_CANNOT_SUPPLY } },
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Shared outcomes — used by the initial supply pathway.
// ─────────────────────────────────────────────────────────────────────────────

export const oralSemaglutideScreeningOutcomes: Record<string, OutcomeNode> = {
  [OSEM_OUT_CANNOT_SUPPLY]: {
    id: OSEM_OUT_CANNOT_SUPPLY,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot supply — oral semaglutide (Wegovy® tablets)",
    reason:
      "An eligibility criterion was not met, an exclusion criterion was triggered, or the patient cannot follow the oral fasting regimen. Supply cannot proceed under this PGD.",
    safetyNetting: [
      "Discuss the reason supply cannot proceed with the patient.",
      "Consider referral to GP or prescriber if clinically appropriate.",
      "If the patient cannot follow the fasting regimen, consider whether injectable semaglutide may be more suitable.",
    ],
  },
  [OSEM_OUT_SUPPLY]: {
    id: OSEM_OUT_SUPPLY,
    type: "outcome",
    outcomeType: "supply",
    title: "Supply complete — Wegovy® 1.5 mg tablets (initial supply)",
    reason:
      "All inclusion criteria met, no exclusion criteria triggered, fasting regimen confirmed suitable, and supply attestation confirmed.",
    drugRef: "oral_semaglutide",
    safetyNetting: [
      "Return in 4 weeks for monthly monitoring review.",
      "Missed dose: skip it and take the next dose the following day. Never take two tablets in one day.",
      "Contact the service immediately for any signs of allergic reaction, severe abdominal pain, or persistent vomiting.",
      "Seek urgent medical attention for signs of pancreatitis: severe, persistent abdominal pain radiating to the back.",
      "Report any sudden change in vision, including partial loss (possible NAION).",
      "Store tablets in the original blister at room temperature. No refrigeration required.",
    ],
  },
};
